import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import api from '@/lib/api';
import { toast } from 'sonner';
import { refreshCreditCounter } from '@/components/CreditCounter';

export default function BillingSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const [status, setStatus] = useState('checking');
    const [kind, setKind] = useState(null);
    const attemptsRef = useRef(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (!sessionId) {
            setStatus('error');
            return;
        }
        const poll = async () => {
            try {
                const r = await api.get(`/billing/checkout/status/${sessionId}`);
                setKind(r.data.kind);
                if (r.data.status === 'completed') {
                    setStatus('done');
                    refreshCreditCounter();
                    return;
                }
                if (r.data.status === 'expired' || r.data.status === 'failed') {
                    setStatus('failed');
                    return;
                }
                attemptsRef.current += 1;
                if (attemptsRef.current >= 8) {
                    setStatus('timeout');
                    return;
                }
                setTimeout(poll, 2000);
            } catch (e) {
                if (attemptsRef.current >= 5) {
                    setStatus('error');
                } else {
                    attemptsRef.current += 1;
                    setTimeout(poll, 2500);
                }
            }
        };
        poll();
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <header className="bg-white border-b">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center"><Logo /></div>
            </header>
            <div className="flex-1 flex items-center justify-center p-6">
                <Card className="w-full max-w-lg">
                    <CardContent className="pt-10 pb-8 text-center">
                        {status === 'checking' && (
                            <>
                                <Loader2 className="h-12 w-12 text-amber-500 mx-auto animate-spin" />
                                <h2 className="mt-4 text-2xl font-display">Confirming your payment…</h2>
                                <p className="mt-2 text-slate-600">This usually takes just a few seconds.</p>
                            </>
                        )}
                        {status === 'done' && (
                            <>
                                <div className="h-14 w-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                                </div>
                                <h2 className="mt-4 text-2xl font-display" data-testid="billing-success-title">You're all set!</h2>
                                <p className="mt-2 text-slate-600">{kind === 'subscription' ? 'Subscription activated. Monthly credits granted.' : kind === 'topup' ? 'Top-up credits added to your balance.' : 'Payment complete.'}</p>
                                <div className="mt-6 flex gap-3 justify-center">
                                    <Button asChild className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="billing-success-to-dashboard">
                                        <Link to="/app/dashboard">Go to dashboard</Link>
                                    </Button>
                                    <Button asChild variant="outline"><Link to="/app/leads">Find leads</Link></Button>
                                </div>
                            </>
                        )}
                        {(status === 'failed' || status === 'error') && (
                            <>
                                <AlertCircle className="h-12 w-12 mx-auto text-red-500" />
                                <h2 className="mt-4 text-2xl font-display">Something went wrong</h2>
                                <p className="mt-2 text-slate-600">We couldn't confirm your payment. If a charge was made, it will be refunded.</p>
                                <Button asChild variant="outline" className="mt-6"><Link to="/pricing">Back to pricing</Link></Button>
                            </>
                        )}
                        {status === 'timeout' && (
                            <>
                                <Loader2 className="h-12 w-12 text-slate-400 mx-auto" />
                                <h2 className="mt-4 text-2xl font-display">Still processing…</h2>
                                <p className="mt-2 text-slate-600">Your payment is taking a bit longer. Check your dashboard in a moment.</p>
                                <Button asChild className="mt-6 bg-slate-900 text-white hover:bg-slate-800"><Link to="/app/dashboard">Go to dashboard</Link></Button>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
